import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Calendar, Plus, Trash2, Wand2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminBulkSchedulePage = () => {
  const { language } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  // Form state
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [schedules, setSchedules] = useState([{ start_date: '' }]);
  const [deliveryMode, setDeliveryMode] = useState('onsite');
  const [featuredCount, setFeaturedCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, citiesRes, categoriesRes] = await Promise.all([
        fetch(`${API}/admin/course-templates`, { credentials: 'include' }),
        fetch(`${API}/admin/training-cities`, { credentials: 'include' }),
        fetch(`${API}/categories`, { credentials: 'include' })
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.filter(t => t.is_active !== false));
      }
      if (citiesRes.ok) {
        const data = await citiesRes.json();
        setCities(data.filter(c => c.is_active !== false));
      }
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category_id === selectedCategory);

  const toggleTemplate = (templateId) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const toggleCity = (cityId) => {
    setSelectedCities(prev => 
      prev.includes(cityId) 
        ? prev.filter(id => id !== cityId)
        : [...prev, cityId]
    );
  };

  const selectAllTemplates = () => {
    setSelectedTemplates(filteredTemplates.map(t => t.template_id));
  };

  const clearAllTemplates = () => {
    setSelectedTemplates([]);
  };

  const selectAllCities = () => {
    setSelectedCities(cities.map(c => c.city_id));
  };

  const clearAllCities = () => {
    setSelectedCities([]);
  };

  const addSchedule = () => {
    setSchedules(prev => [...prev, { start_date: '' }]);
  };

  const removeSchedule = (index) => {
    setSchedules(prev => prev.filter((_, i) => i !== index));
  };

  const updateSchedule = (index, date) => {
    setSchedules(prev => prev.map((s, i) => i === index ? { start_date: date } : s));
  };

  const handleSubmit = async () => {
    // Validation
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one course template');
      return;
    }
    if (selectedCities.length === 0) {
      toast.error('Please select at least one city');
      return;
    }
    const validSchedules = schedules.filter(s => s.start_date);
    if (validSchedules.length === 0) {
      toast.error('Please add at least one schedule date');
      return;
    }

    setCreating(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/admin/course-sessions/quick-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template_ids: selectedTemplates,
          city_ids: selectedCities,
          start_dates: validSchedules.map(s => s.start_date),
          delivery_mode: deliveryMode,
          featured_first_n: featuredCount
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        toast.success(`Created ${data.created_count} sessions!`);
      } else {
        toast.error(data.detail || 'Failed to create sessions');
      }
    } catch (error) {
      toast.error('Failed to create sessions');
    } finally {
      setCreating(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.category_id === categoryId);
    return cat ? cat.name : categoryId;
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-bulk-schedule-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">Bulk Session Scheduling</h1>
        <p className="text-[#6B7280]">Create course sessions for multiple templates at once</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>1. Select Templates</span>
              <Badge variant="outline">{selectedTemplates.length} selected</Badge>
            </CardTitle>
            <div className="flex gap-2 pt-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={selectAllTemplates}>Select All</Button>
              <Button variant="ghost" size="sm" onClick={clearAllTemplates}>Clear</Button>
            </div>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            <div className="space-y-2">
              {filteredTemplates.map(template => (
                <div 
                  key={template.template_id}
                  className={`p-2 rounded border cursor-pointer transition-colors ${
                    selectedTemplates.includes(template.template_id)
                      ? 'bg-[#33AADD]/10 border-[#33AADD]'
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => toggleTemplate(template.template_id)}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={selectedTemplates.includes(template.template_id)}
                      onCheckedChange={() => toggleTemplate(template.template_id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{template.title}</p>
                      <p className="text-xs text-[#6B7280]">{template.duration_days} days</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cities Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>2. Select Cities</span>
              <Badge variant="outline">{selectedCities.length} selected</Badge>
            </CardTitle>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={selectAllCities}>Select All</Button>
              <Button variant="ghost" size="sm" onClick={clearAllCities}>Clear</Button>
            </div>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            <div className="space-y-2">
              {cities.map(city => (
                <div 
                  key={city.city_id}
                  className={`p-2 rounded border cursor-pointer transition-colors ${
                    selectedCities.includes(city.city_id)
                      ? 'bg-[#33AADD]/10 border-[#33AADD]'
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => toggleCity(city.city_id)}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={selectedCities.includes(city.city_id)}
                      onCheckedChange={() => toggleCity(city.city_id)}
                    />
                    <div>
                      <p className="text-sm font-medium">{city.name}</p>
                      <p className="text-xs text-[#6B7280]">{city.name_ar}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Schedules & Options */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>3. Schedule Dates</span>
              <Badge variant="outline">{schedules.filter(s => s.start_date).length} dates</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {schedules.map((schedule, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Input
                    type="date"
                    value={schedule.start_date}
                    onChange={(e) => updateSchedule(index, e.target.value)}
                    className="flex-1"
                  />
                  {schedules.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeSchedule(index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addSchedule} className="w-full">
                <Plus className="h-4 w-4 me-1" />
                Add Date
              </Button>
            </div>

            <div className="space-y-2 pt-4 border-t">
              <Label>Delivery Mode</Label>
              <Select value={deliveryMode} onValueChange={setDeliveryMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="onsite">On-site</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Mark first N as Featured</Label>
              <Input
                type="number"
                min="0"
                value={featuredCount}
                onChange={(e) => setFeaturedCount(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-[#6B7280]">Featured courses appear at the top of listings</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary & Submit */}
      <Card className="mt-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-[#6B7280]">Templates:</span>
                <span className="font-medium ms-1">{selectedTemplates.length}</span>
              </div>
              <div>
                <span className="text-[#6B7280]">Cities:</span>
                <span className="font-medium ms-1">{selectedCities.length}</span>
              </div>
              <div>
                <span className="text-[#6B7280]">Dates:</span>
                <span className="font-medium ms-1">{schedules.filter(s => s.start_date).length}</span>
              </div>
              <div>
                <span className="text-[#6B7280]">Total sessions:</span>
                <span className="font-bold ms-1 text-[#33AADD]">
                  {selectedTemplates.length * selectedCities.length * schedules.filter(s => s.start_date).length}
                </span>
              </div>
            </div>

            <Button 
              className="btn-primary" 
              onClick={handleSubmit}
              disabled={creating || selectedTemplates.length === 0 || selectedCities.length === 0}
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 me-2" />
                  Create Sessions
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Result */}
      {result && (
        <Card className="mt-4 border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Check className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-800">Sessions Created Successfully</h3>
                <p className="text-sm text-green-700 mt-1">{result.message}</p>
                <div className="flex gap-4 mt-2 text-sm text-green-600">
                  <span>Created: {result.created_count}</span>
                  <span>Skipped (duplicates): {result.skipped_count}</span>
                  {result.featured_count > 0 && <span>Featured: {result.featured_count}</span>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminBulkSchedulePage;
